// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import DownloadIcon from '@mui/icons-material/Download'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { useStore, useTranslate } from 'react-admin'
import { trackNow } from '../analytics/usageTracker'
import { formatGlucoseForExport } from '../common'
import { stableFallbackStartMs, stableWindowEndMs } from '../dashboard/components/petDetailWindow'
import { useDeviceMetrics } from '../hooks/useDeviceMetrics'
import { useExportSelection } from '../hooks/useExportSelection'
import { neutralizeCsvFormula } from '../utils/csvFormula'
import { type GlucoseUnit } from '../utils/glucoseUnits'
import { useDateFormatter } from '../utils/localeDate'
import { SHARED_METRICS_PER_PAGE } from '../utils/paginationUtils'
import { countMaskedReadings, recordExportEvent } from '../utils/recordExportEvent'
import { formatTimezoneForDisplay } from '../utils/timezoneUtils'
import { ReportDeviceSelector } from './ReportDeviceSelector'

// Columns intentionally excluded from the export (internal/unstructured fields)
const EXCLUDED_COLUMNS = ['notes', 'srcid']

/**
 * Build CSV content from metric records.
 *
 * Follows the project CSV export convention (see CLAUDE.md / MetricList):
 * - glucose goes through `formatGlucoseForExport` (BGM correction, then the
 *   SAME out-of-range masking the screen uses) and is rendered in `unit`
 *   (mg/dL → integer, mmol/L → 1 decimal). Callers state the unit in the file
 *   name so the column is never ambiguous. Smoothing is NOT applied — that is
 *   a chart-only transform and would fabricate readings in a data record.
 *   Out-of-range rows export as "<40"/">540" (or "<2.2"/">30"), NOT as a bare
 *   clamped number: a cell reading "40" is indistinguishable from a genuine
 *   40 mg/dL, which is what a sensor drop-out used to look like here.
 * - timestamps converted from Unix seconds to ISO strings
 * - values are quoted and quote-escaped
 *
 * Exported for unit testing.
 */
export function buildMetricsCsv(
  metrics: Record<string, any>[],
  unit: GlucoseUnit = 'mmol/L',
  // Metrics arrive at depth 0 (P73-1), so `device` is an id: name it from here.
  deviceNames?: ReadonlyMap<number | string, string>,
): string {
  if (!metrics.length) return ''
  const keys = Object.keys(metrics[0]).filter((key) => !EXCLUDED_COLUMNS.includes(key))
  const header = keys.join(',')
  const rows = metrics.map((record) =>
    keys
      .map((key) => {
        let value = record[key]
        if (key === 'timestamp' && typeof value === 'number') {
          // API timestamps are Unix seconds — convert to ms for Date
          value = new Date(value * 1000).toISOString()
        } else if (key === 'glucose' && typeof value === 'number') {
          value = formatGlucoseForExport(value, unit)
        } else if (key === 'device' && value !== null && typeof value === 'object') {
          // Populated device relation — export the device name, not [object Object]
          value = value.name ?? value.id ?? ''
        } else if (key === 'device' && value != null && deviceNames?.has(value)) {
          value = deviceNames.get(value)
        } else if (key === 'timezone') {
          // Raw values use the JS getTimezoneOffset() minutes-west convention
          // (e.g. -480 = UTC+8); export a human-readable UTC offset instead
          value = formatTimezoneForDisplay(value)
        }
        // P72-2: owner-writable text (a device name) must not run as a formula.
        return `"${String(neutralizeCsvFormula(value) ?? '').replace(/"/g, '""')}"`
      })
      .join(','),
  )
  return [header, ...rows].join('\r\n')
}

interface GlucoseCSVExportProps {
  petId: string | number
  deviceId: string | number
  startDate?: Date
  endDate?: Date
  size?: 'small' | 'medium' | 'large'
}

/**
 * Export glucose readings as CSV. The button opens the same range picker as
 * the PDF report (vic/vetpo#56): the sensors to export (most recent
 * preselected) or a custom range, so the two exports never cover different
 * data for the same choice. Before the dialog opens the page's own device and
 * window are used. Glucose values are BGM-corrected and out-of-range-masked exactly as on
 * screen, rendered in the portal's active display unit (mmol/L or mg/dL); the
 * chosen unit is stated in the file name. Data flows through
 * useDeviceMetrics — the same hook the charts and PDF report use — so virtual
 * "All Devices" selection and dislodgement filtering behave identically.
 */
export const GlucoseCSVExport: React.FC<GlucoseCSVExportProps> = ({
  petId,
  deviceId,
  startDate: startDateProp,
  endDate: endDateProp,
  size = 'medium',
}) => {
  const translate = useTranslate()
  const fmtDate = useDateFormatter()
  const [glucoseUnit] = useStore<GlucoseUnit>('glucoseUnit', 'mmol/L')

  // S4 guard: stable per-mount defaults — fresh Date objects per render would
  // change useDeviceMetrics' query filter and churn the react-query cache.
  // Bucketed (stableWindowEndMs) so remounts within the same 5-minute window
  // also reuse cached queries (2026-07-10 request storm).
  const defaultStart = useMemo(
    () => startDateProp ?? new Date(stableFallbackStartMs(Date.now())),
    [startDateProp],
  )
  const defaultEnd = useMemo(
    () => endDateProp ?? new Date(stableWindowEndMs(Date.now())),
    [endDateProp],
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const selection = useExportSelection({
    petId,
    deviceId,
    defaultStart,
    defaultEnd,
  })
  const startDate = selection.start
  const endDate = selection.end

  // Same query shape as GlucosePDFExport so React Query shares the cache
  const {
    data: metrics = [],
    isLoading,
    isTruncated,
    error: loadError,
    devices,
  } = useDeviceMetrics({
    deviceId: selection.queryDeviceId,
    deviceIds: selection.queryDeviceIds,
    petId,
    startDate,
    endDate,
    sort: { field: 'timestamp', order: 'ASC' },
    pagination: { page: 1, perPage: SHARED_METRICS_PER_PAGE },
  })

  const handleExport = useCallback(() => {
    if (!metrics.length) return
    const deviceNames = new Map((devices ?? []).map((d) => [d.id, d.name] as const))
    const csvContent = buildMetricsCsv(metrics, glucoseUnit, deviceNames)
    // The glucose column is BGM-corrected and rendered in the active display
    // unit; the file name records which unit so the column is never ambiguous.
    const unitSlug = glucoseUnit === 'mg/dL' ? 'mg-per-dL' : 'mmol-per-L'
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    // A truncated window is disclosed in the FILE NAME, not inside the data:
    // an in-band marker row would be read as a reading by any CSV consumer,
    // while the tooltip warning dies with the browser tab. The suffix is the
    // only disclosure that travels with a saved copy.
    const partialSlug = isTruncated ? '-partial' : ''
    const deviceSlug =
      selection.selectedIds.length === 1
        ? String(selection.selectedIds[0])
        : selection.selectedIds.length > 1
          ? selection.selectedIds.join('+')
          : selection.active
            ? 'all'
            : String(deviceId)
    link.download = `pet-${petId}-device-${deviceSlug}-metrics-${unitSlug}${partialSlug}.csv`
    link.click()
    URL.revokeObjectURL(url)
    // A clinical record just left the building — record that it did. Fire and
    // forget: an audit failure must never deny the vet their export (see
    // recordExportEvent).
    trackNow({ type: 'report_exported', detail: 'csv', pet: Number(petId) })
    setDialogOpen(false)
    recordExportEvent({
      petId,
      deviceId:
        selection.selectedIds.length === 1 ? selection.selectedIds[0] : selection.queryDeviceId,
      devices: selection.queryDeviceIds,
      selectionMode: selection.selectionMode ?? undefined,
      format: 'csv',
      unit: glucoseUnit,
      rangeStart: startDate,
      rangeEnd: endDate,
      rowCount: metrics.length,
      maskedCount: countMaskedReadings(metrics, glucoseUnit),
      // The vet is already warned in the UI that a capped window is incomplete;
      // record it too, or the audit row cannot tell a disclosed truncation from
      // tampering.
      truncated: isTruncated,
    })
  }, [metrics, devices, petId, deviceId, glucoseUnit, startDate, endDate, isTruncated, selection])

  const { activate } = selection
  const openDialog = useCallback(() => {
    activate()
    setDialogOpen(true)
  }, [activate])

  const hasDeviceRows = selection.rows.some((r) => r.first && r.last)
  const canExport =
    !selection.rowsLoading &&
    !isLoading &&
    !loadError &&
    metrics.length > 0 &&
    !(selection.mode === 'devices' && hasDeviceRows && selection.selectedIds.length === 0)

  const dialog = (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('pos.export_selection.csv_dialog_title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {isTruncated && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {translate('pos.reports.truncated_warning')}
            </Alert>
          )}
          <ReportDeviceSelector
            rows={selection.rows}
            isLoading={selection.rowsLoading}
            error={selection.rowsError}
            mode={selection.mode}
            onModeChange={selection.setMode}
            selectedIds={selection.selectedIds}
            onSelectedIdsChange={selection.setSelectedIds}
            customStart={selection.customStart}
            customEnd={selection.customEnd}
            onCustomRangeChange={selection.setCustomRange}
            dataRange={selection.petDataRange}
          />
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('pos.export_selection.report_window')}:
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              {fmtDate(startDate, 'short')} — {fmtDate(endDate, 'short')}
            </Typography>
          </Box>
          {loadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {translate('pos.reports.csv_load_error')}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDialogOpen(false)}>{translate('ra.action.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={!canExport}
          startIcon={
            isLoading || selection.rowsLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
        >
          {translate('pos.reports.export_csv')}
        </Button>
      </DialogActions>
    </Dialog>
  )

  // Truncated window (server limit clamp): still allow the export, but say
  // the file is incomplete — a silent partial CSV reads as complete data.
  const button = (
    <Button
      variant="outlined"
      color={loadError ? 'error' : isTruncated ? 'warning' : 'primary'}
      startIcon={<DownloadIcon />}
      onClick={openDialog}
      // A failed load is said out loud (tooltip below), never a mute disable.
      disabled={!!loadError}
      size={size}
    >
      {translate('pos.reports.export_csv')}
    </Button>
  )

  // A failed load previously rendered the same mute disabled button as
  // "no data in this window" — indistinguishable states for the vet.
  if (loadError) {
    return (
      <>
        {dialog}
        <Tooltip title={translate('pos.reports.csv_load_error')}>
          <span>{button}</span>
        </Tooltip>
      </>
    )
  }

  if (!isTruncated) {
    return (
      <>
        {dialog}
        {button}
      </>
    )
  }
  return (
    <>
      {dialog}
      <Tooltip title={translate('pos.reports.truncated_warning')}>
        <span>{button}</span>
      </Tooltip>
    </>
  )
}
